/**
 * Skill lattice for Soft multi-asset dispatch.
 * Skills drive: matching, route profile, coverage, map language.
 * Soft≠Live.
 */

/** Canonical skills operators recognise — not vendor jargon dump. */
export const SKILL = {
  observe: {
    id: "observe",
    label: "Observe",
    brief: "See the scene",
    coverM: 180,
  },
  thermal: {
    id: "thermal",
    label: "Thermal",
    brief: "Heat anomaly",
    coverM: 140,
  },
  patrol: {
    id: "patrol",
    label: "Patrol",
    brief: "Move on ground",
    coverM: 0,
  },
  challenge: {
    id: "challenge",
    label: "Challenge",
    brief: "Human verify",
    coverM: 0,
  },
  alert: {
    id: "alert",
    label: "Alert",
    brief: "Siren / push",
    coverM: 260,
  },
  overwatch: {
    id: "overwatch",
    label: "Overwatch",
    brief: "Aerial eyes",
    coverM: 0,
  },
};

export const SKILL_ORDER = ["observe", "thermal", "patrol", "challenge", "alert", "overwatch"];

/**
 * Route profiles bound to skill substrate (what physics carries the job).
 * walk = OSM foot · bike = OSM cycle paths · drive = OSM roads · air = corridor · static = camera/siren.
 */
export const ROUTE_BY_KIND = {
  person: "walk",
  robot: "bike", // service / path – closer to UGV than highway drive for site work
  drone: "air",
  device: "static",
};

/** Colour tokens for ops map / desk — tied to skill transit class, not rainbow candy. */
export const PROFILE_INK = {
  walk: "#7EC8B8",
  bike: "#D4A574",
  drive: "#E8B86D",
  air: "#A89BFF",
  static: "#8FA39E",
};

export const KIND_INK = {
  person: "#7EC8B8",
  robot: "#D4A574",
  drone: "#A89BFF",
  device: "#8FA39E",
};

/**
 * Seed roster around the live site centre (always in viewport).
 * Skills + routeProfile drive dispatch; OSRM resolves real OSM paths from these starts.
 */
export function buildSkillRoster(origin) {
  const c = origin && origin.length === 2 ? origin : [101.4076, 3.0418];
  const at = (dLng, dLat) => [c[0] + dLng, c[1] + dLat];

  const row = (spec) => ({
    ...spec,
    ll: spec.ll.slice(),
    home: spec.ll.slice(),
    color: KIND_INK[spec.kind] || PROFILE_INK[spec.routeProfile] || "#8FA39E",
    free: true,
    path: null,
    pathI: 0,
    missionId: null,
    etaS: 0,
    routeProfile: spec.routeProfile || ROUTE_BY_KIND[spec.kind] || "walk",
    routeMeta: null,
    skills: (spec.skills || []).slice(),
  });

  return [
    row({
      id: "GUARD-A1",
      kind: "person",
      tier: "L1 foot patrol",
      label: "Guard A1",
      skills: ["patrol", "challenge"],
      speed: 1.35,
      ll: at(-0.0011, 0.0006),
      routeProfile: "walk",
    }),
    row({
      id: "GUARD-B2",
      kind: "person",
      tier: "L2 lead",
      label: "Guard B2",
      skills: ["patrol", "challenge", "alert"],
      speed: 1.4,
      ll: at(0.0014, 0.0009),
      routeProfile: "walk",
    }),
    row({
      id: "SUP-C1",
      kind: "person",
      tier: "L3 supervisor",
      label: "Supervisor C1",
      skills: ["challenge", "alert"],
      speed: 1.2,
      ll: at(0.0002, -0.0012),
      routeProfile: "walk",
    }),
    row({
      id: "CAM-T1-01",
      kind: "device",
      tier: "T1 edge cam",
      label: "Cam T1-01",
      skills: ["observe"],
      speed: 0,
      ll: at(-0.0009, 0.0015),
      routeProfile: "static",
      fixed: true,
    }),
    row({
      id: "CAM-T2-04",
      kind: "device",
      tier: "T2 PTZ",
      label: "Cam T2-04",
      skills: ["observe", "thermal"],
      speed: 0,
      ll: at(0.0012, -0.0008),
      routeProfile: "static",
      fixed: true,
    }),
    row({
      id: "SIR-T3-01",
      kind: "device",
      tier: "T3 siren mesh",
      label: "Siren T3",
      skills: ["alert"],
      speed: 0,
      ll: at(-0.0015, -0.0005),
      routeProfile: "static",
      fixed: true,
    }),
    row({
      id: "GW-T3",
      kind: "device",
      tier: "T3 gateway",
      label: "Gateway",
      skills: ["alert"],
      speed: 0,
      ll: at(0, 0),
      routeProfile: "static",
      fixed: true,
    }),
    row({
      id: "UAV-L1",
      kind: "drone",
      tier: "L1 scout",
      label: "UAV Scout",
      skills: ["overwatch", "observe"],
      speed: 14,
      ll: at(0.0006, 0.0018),
      routeProfile: "air",
    }),
    row({
      id: "UAV-L2",
      kind: "drone",
      tier: "L2 thermal",
      label: "UAV Thermal",
      skills: ["overwatch", "thermal", "observe"],
      speed: 12,
      ll: at(-0.0018, 0.0011),
      routeProfile: "air",
    }),
    row({
      id: "UGV-R1",
      kind: "robot",
      tier: "R1 patrol",
      label: "UGV Patrol",
      skills: ["patrol", "observe"],
      speed: 2.4,
      ll: at(0.0018, -0.0004),
      routeProfile: "bike",
    }),
    row({
      id: "UGV-R2",
      kind: "robot",
      tier: "R2 quad",
      label: "UGV Quad",
      skills: ["patrol", "observe", "alert"],
      speed: 2.0,
      ll: at(-0.0006, -0.0016),
      routeProfile: "bike",
    }),
  ];
}

/**
 * Incidents demand skills — destinations are Soft edge devices (p2p).
 * Soft≠Live · anchors are kind=device ids from buildSkillRoster.
 */
export function skillIncidentCatalog(origin) {
  const c = origin && origin.length === 2 ? origin : [101.4076, 3.0418];
  void c; // origin retained for call-site parity; anchor is device id
  return [
    {
      id: "perimeter_breach",
      title: "Perimeter breach",
      needs: ["observe", "patrol", "alert"],
      severity: "high",
      anchorDeviceId: "CAM-T1-01",
      place: "SE perimeter",
    },
    {
      id: "yard_motion",
      title: "Yard motion cluster",
      needs: ["observe", "patrol"],
      severity: "mid",
      anchorDeviceId: "CAM-T2-04",
      place: "north yard",
    },
    {
      id: "roof_thermal",
      title: "Roof thermal spike",
      needs: ["thermal", "overwatch"],
      severity: "mid",
      anchorDeviceId: "CAM-T2-04",
      place: "roof band",
    },
    {
      id: "gate_queue",
      title: "Gate challenge needed",
      needs: ["challenge", "patrol"],
      severity: "low",
      anchorDeviceId: "GW-T3",
      place: "main gate",
    },
    {
      id: "silo_access",
      title: "Silo access alert",
      needs: ["observe", "alert", "challenge"],
      severity: "high",
      anchorDeviceId: "SIR-T3-01",
      place: "east spur",
    },
  ];
}

/**
 * Pure skill match (before road resolve).
 * 1) station assets covering skill within coverM
 * 2) greedy free mobiles by rough ETA (haversine × detour × skill speed)
 */
export function matchSkills(incident, assets) {
  const remaining = new Set(incident.needs || []);
  const team = [];
  const used = new Set();

  const onStation = assets.filter((a) => a.fixed && a.free);
  for (const a of onStation) {
    const hit = a.skills.filter((s) => remaining.has(s));
    if (!hit.length) continue;
    const cover = Math.max(...hit.map((s) => (SKILL[s] && SKILL[s].coverM) || 0), 80);
    const d = haversineFrom(a.ll, incident.ll);
    if (d > cover) continue;
    hit.forEach((s) => remaining.delete(s));
    team.push({
      assetId: a.id,
      kind: "on_station",
      skillsUsed: hit,
      roughEta: 0,
    });
    used.add(a.id);
  }

  if (!remaining.size) {
    return { ok: true, team, missing: null, remaining: [] };
  }

  const mobiles = assets.filter((a) => a.free && !a.fixed && !used.has(a.id));
  const still = [...remaining];
  for (const need of still) {
    if (!remaining.has(need)) continue;
    let best = null;
    for (const a of mobiles) {
      if (used.has(a.id)) continue;
      if (!a.skills.includes(need)) continue;
      const roadish = a.routeProfile === "air" ? 1.0 : 1.35;
      const dist = haversineFrom(a.ll, incident.ll) * roadish;
      const eta = a.speed > 0 ? dist / a.speed : 1e9;
      if (!best || eta < best.eta) {
        best = {
          assetId: a.id,
          kind: "dispatch",
          skillsUsed: a.skills.filter((s) => remaining.has(s)),
          roughEta: eta,
          eta,
        };
      }
    }
    if (!best) {
      return {
        ok: false,
        team,
        missing: need,
        remaining: [...remaining],
      };
    }
    best.skillsUsed.forEach((s) => remaining.delete(s));
    used.add(best.assetId);
    team.push(best);
  }

  return {
    ok: true,
    team,
    missing: null,
    remaining: [...remaining],
    maxRoughEta: Math.max(0, ...team.map((t) => t.roughEta || 0)),
  };
}

function haversineFrom(a, b) {
  // local import avoid cycle — inlined small
  const R = 6371000;
  const toR = (d) => (d * Math.PI) / 180;
  const dLat = toR(b[1] - a[1]);
  const dLng = toR(b[0] - a[0]);
  const la1 = toR(a[1]);
  const la2 = toR(b[1]);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}
